import { metricValue } from './presentation';

export const DEFAULT_TABLE_PAGE_SIZE = 10;
export const TIMELINE_PAGE_SIZE = 20;

const WORKSPACE_QUERY_STATE_KEY = 'member-training-workspace-query@1';

export const ACTIVITY_TIMELINE_EVIDENCE_TYPES = [
  'course_execution',
  'activity_execution',
  'diet_context_fact',
  'sleep_context_fact',
  'weight_context_fact',
  'mood_context_fact',
  'medicine_context_fact',
  'member_goal_change',
  'action_motion_observation',
  'course_motion_observation',
  'capability_account_snapshot',
];

const TIMELINE_PRESENTATION = {
  course_execution: {
    title: '完成一节训练课',
    category: '上课',
    color: 'green',
  },
  activity_execution: {
    title: '完成一次运动打卡',
    category: '运动',
    color: 'blue',
  },
  diet_context_fact: {
    title: '饮食打卡',
    category: '生活打卡',
    color: 'orange',
  },
  sleep_context_fact: {
    title: '睡眠打卡',
    category: '恢复记录',
    color: 'purple',
  },
  weight_context_fact: {
    title: '身体指标打卡',
    category: '身体记录',
    color: 'cyan',
  },
  mood_context_fact: {
    title: '心情打卡',
    category: '状态记录',
    color: 'green',
  },
  medicine_context_fact: {
    title: '用药打卡',
    category: '健康记录',
    color: 'red',
  },
  member_goal_change: {
    title: '运动目标发生变化',
    category: '目标',
    color: 'gold',
  },
  action_motion_observation: {
    title: '生成动作评估',
    category: '动作评估',
    color: 'blue',
  },
  course_motion_observation: {
    title: '生成课程评估',
    category: '课程评估',
    color: 'blue',
  },
  capability_account_snapshot: {
    title: '运动能力更新',
    category: '运动能力',
    color: 'purple',
  },
};

const TIMELINE_DETAIL_FIELD_LABELS = {
  title: '记录标题',
  eventTime: '记录日期',
  courseModality: '课程形式',
  classDurationSeconds: '课程时长',
  completeActionPercentage: '动作完成度',
  rawActivityName: '运动名称',
  rawActivityType: '运动类型',
  normalizedActivityType: '归一化运动类型',
  durationSeconds: '运动时长',
  distanceValue: '运动距离',
  kcal: '消耗热量',
  weight: '体重',
  weightValue: '体重',
  dimensionsType: '身体指标',
  dimensionsValue: '指标数值',
  dateIndex: '记录日期',
  dietType: '饮食类型',
  dietName: '饮食内容',
  dietTime: '饮食时间',
  sleepType: '睡眠类型',
  sleepTime: '睡眠时间',
  mood: '心情',
  moodName: '心情',
  moodType: '心情类型',
  medicineName: '用药名称',
  sportLocation: '运动地点',
  remark: '备注',
};

const COURSE_MODALITY_LABELS = {
  xplan_on_camera: 'XPlan 入镜训练',
  ai_follow_along: 'AI 跟练课',
  ai_manual_record: 'AI 训练课',
  live_online_1v1: '线上 1V1 真人指导',
  live_coaching_other: '真人指导课',
};

const ACTIVITY_TYPE_LABELS = {
  aerobic_endurance: '有氧耐力训练',
  strength_training: '力量训练',
  conditioning: '体能训练',
  mobility_mind_body: '瑜伽或身心训练',
  course_training: '课程训练',
  custom_unclassified: '自定义运动',
};

const BODY_METRIC_PRESENTATION = {
  weight: { name: '体重', unit: 'kg' },
  waist: { name: '腰围', unit: 'cm' },
  thigh: { name: '大腿围', unit: 'cm' },
  calf: { name: '小腿围', unit: 'cm' },
  hip: { name: '臀围', unit: 'cm' },
  chest: { name: '胸围', unit: 'cm' },
  arm: { name: '手臂围', unit: 'cm' },
};

export const DIMENSION_LABELS = {
  coordination: '协调性',
  lower_body_stability: '下肢稳定性',
  trunk_stability: '躯干稳定性',
  upper_body_stability: '上肢稳定性',
  upper_body_mobility: '上肢活动度',
  lower_body_mobility: '下肢活动度',
  trunk_mobility: '躯干活动度',
  lower_body_strength: '下肢力量',
  upper_body_strength: '上肢力量',
  cardiovascular_endurance: '心肺耐力',
};

export const SCORE_STATUS_LABELS = {
  available: '已评估',
  estimated: '问卷估算',
  training_observed: '训练趋势观察',
  training_confirmed: '训练事实已确认',
  training_inferred: '训练证据评估',
  motion_measured: '动作实测评估',
  mixed_evidence: '综合证据评估',
  training_collecting: '训练证据积累中',
  motion_collecting: '实测证据积累中',
  not_calibrated: '暂不能评分',
  insufficient_coverage: '覆盖不足',
  insufficient_measurements: '数据不足',
  not_evaluated: '尚未评估',
};

export const CAPABILITY_SOURCE_LABELS = {
  questionnaire: '初始问卷',
  training_inferred: '正常训练观察',
  motion_measured: 'VK / 视频实测',
  mixed_evidence: '多源综合',
  training_collecting: '证据积累中',
  motion_collecting: '实测证据积累中',
};

export const CAPABILITY_CONFIDENCE_LABELS = {
  high: '高可信',
  medium: '中等可信',
  low: '初步判断',
};

export const CAPABILITY_CHANGE_LABELS = {
  initial: '首次建立',
  increased: '能力提升',
  unchanged: '保持稳定',
  decreased: '有所下降',
  stable_within_mdc: '变化在误差内',
};

export const capabilitySourceText = (source) =>
  CAPABILITY_SOURCE_LABELS[source] || '已记录证据';

export const capabilityConfidenceText = (confidence) =>
  CAPABILITY_CONFIDENCE_LABELS[confidence] || '待积累';

/** API 内部能力标尺为 0-100；页面统一展示为 0-10，并兼容历史整数 score。 */
export const capabilityScore10 = (record = {}) => {
  const current = Number(record.currentScore);
  if (
    record.currentScore !== null &&
    record.currentScore !== undefined &&
    record.currentScore !== '' &&
    Number.isFinite(current)
  ) {
    return Math.max(0, Math.min(10, current / 10));
  }
  const legacy = Number(record.score);
  return record.score !== null &&
    record.score !== undefined &&
    record.score !== '' &&
    Number.isFinite(legacy)
    ? Math.max(0, Math.min(10, legacy))
    : null;
};

export const capabilityScoreText = (record = {}) => {
  const score = capabilityScore10(record);
  if (score === null) return '-';
  const rounded = Math.round(score * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
};

export const capabilityScoreDelta10 = (record = {}) => {
  const precise = Number(record.currentScoreDelta);
  if (
    record.currentScoreDelta !== null &&
    record.currentScoreDelta !== undefined &&
    record.currentScoreDelta !== '' &&
    Number.isFinite(precise)
  ) {
    return precise / 10;
  }
  const legacy = Number(record.scoreDelta);
  return record.scoreDelta !== null &&
    record.scoreDelta !== undefined &&
    record.scoreDelta !== '' &&
    Number.isFinite(legacy)
    ? legacy
    : null;
};

export const capabilityQualityText = (value) => {
  const ratio = Number(value);
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    !Number.isFinite(ratio)
  )
    return '-';
  return `${Math.round(Math.max(0, Math.min(1, ratio)) * 100)}%`;
};

export const capabilityChangeText = (record = {}) => {
  const delta = capabilityScoreDelta10(record);
  if (record.changeType === 'initial') return '首次建立';
  if (record.changeType === 'stable_within_mdc') {
    if (delta !== null && delta !== 0) {
      const value = Math.round(delta * 10) / 10;
      if (value === 0) return '误差内稳定';
      return `${value > 0 ? '+' : ''}${value}（观察）`;
    }
    return '误差内稳定';
  }
  if (delta !== null && delta !== 0) {
    const value = Math.round(delta * 10) / 10;
    return `${value > 0 ? '+' : ''}${value} 分`;
  }
  if (record.changeType === 'unchanged' || delta === 0) return '持平';
  return CAPABILITY_CHANGE_LABELS[record.changeType] || '待比较';
};

export const RECORD_STATUS_LABELS = {
  generating: '评估生成中',
  succeeded: '分析完成',
  success: '分析完成',
  completed: '已完成',
  pending: '等待处理',
  processing: '处理中',
  failed: '分析失败',
  unavailable: '暂不可用',
};

export const COURSE_CATEGORY_LABELS = {
  1: '训练课',
  2: 'AI 自练课',
  3: '指导课',
};

export const TRAINING_MANNER_LABELS = {
  1: '线上指导',
  2: '线下指导',
  3: '教练创建的线下推广课',
  4: '用户创建的线下自由训练',
};

export const TRAINING_CONTEXT_CARDS = [
  { key: 'sleep', label: '睡眠', tone: 'purple' },
  { key: 'weight', label: '体重', tone: 'blue' },
  { key: 'diet', label: '饮食', tone: 'orange' },
  { key: 'mood', label: '心情', tone: 'green' },
  { key: 'medicine', label: '用药', tone: 'red' },
];

export const COMMUNICATION_SOURCE_LABELS = {
  questionnaire: '会员问卷',
  workwx_chat: '企微训练群',
  guided_course: '1V1 真人课',
  live_coaching: '1V1 真人课',
};

export const COMMUNICATION_CATEGORY_LABELS = {
  health_safety: '健康与安全',
  goal: '训练目标',
  adherence: '训练执行',
  training_response: '训练反应',
  action_performance: '动作表现',
  preference: '训练偏好',
  lifestyle_context: '生活影响',
  readiness_motivation: '状态与动力',
  training_logistics: '训练安排',
  coach_decision: '教练调整',
  follow_up: '后续跟进',
};

export const COMMUNICATION_TAG_LABELS = {
  pain_report: '疼痛反馈',
  discomfort_report: '不适反馈',
  injury_report: '伤病反馈',
  health_condition_report: '健康情况',
  training_restriction: '训练限制',
  goal_stated: '训练目标',
  goal_changed: '目标变化',
  missed_training: '未完成训练',
  training_barrier: '训练阻碍',
  training_commitment: '训练承诺',
  rpe_report: '主观强度反馈',
  fatigue_report: '疲劳反馈',
  soreness_report: '酸痛反馈',
  recovery_report: '恢复反馈',
  sleep_impact: '睡眠影响',
  action_problem: '动作问题',
  coach_cue: '教练提示',
  correction_response: '纠正反馈',
  load_or_rep_report: '重量或次数',
  explicit_like: '明确喜欢',
  explicit_dislike: '明确不喜欢',
  sleep_context: '睡眠情况',
  nutrition_context: '饮食情况',
  medication_context: '用药情况',
  weight_context: '体重情况',
  work_or_life_stress: '工作生活压力',
  motivation_report: '训练动力',
  training_confidence: '训练信心',
  training_mood: '训练情绪',
  readiness_report: '训练准备度',
  time_constraint: '时间限制',
  location_constraint: '地点限制',
  equipment_constraint: '器械限制',
  schedule_confirmation: '训练安排确认',
  course_or_coach_feedback: '课程或教练反馈',
  intensity_adjustment: '强度调整',
  action_substitution: '动作替换',
  frequency_adjustment: '频率调整',
  training_pause: '暂停训练',
  todo: '待办事项',
  reassessment: '需要复评',
  member_feedback_request: '需要会员反馈',
};

export const COMMUNICATION_STATUS_LABELS = {
  current: '当前有效',
  resolved: '已经缓解',
  historical: '历史情况',
  planned: '计划事项',
  unknown: '状态待确认',
};

export const COMMUNICATION_ASSERTION_LABELS = {
  member_reported: '会员自述',
  coach_observed: '教练观察',
  coach_instructed: '教练指导',
  system_derived: '系统归纳',
  unattributed: '来源未区分',
};

export const PROFILE_FIELDS_ALREADY_SHOWN = new Set([
  '性别',
  '年龄',
  '身高',
  '体重',
  '运动目标',
  '当前规律运动',
  '每天静坐时长',
]);

export const WIDE_PROFILE_FIELD_KEYS = new Set([
  '周运动目标',
  '特殊身体情况',
  '伤病痛',
]);

export function succeeded(response) {
  return response && (response.code === '00000' || response.code === '0');
}

export function textOrDash(value) {
  return value === null || value === undefined || value === '' ? '-' : value;
}

export function memberDisplayName(identity) {
  const candidate = [identity?.wxNickname, identity?.nickname].find(
    (value) => value !== null && value !== undefined && String(value).trim(),
  );
  return candidate === undefined ? '-' : String(candidate).trim();
}

export function hasRecordedValue(value) {
  if (value === null || value === undefined || value === '') return false;
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) return numericValue > 0;
  return !['-', '/'].includes(String(value).trim());
}

export function profileTimeText(value) {
  if (value === null || value === undefined || value === '') return '-';
  return String(value).replace(/^填写时间[：:]\s*/, '');
}

export function timeText(value) {
  if (value === null || value === undefined || value === '') return '-';
  const text = String(value).trim();
  const normalized = /^\d+$/.test(text) ? Number(text) : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

export function parseJson(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

export function confidenceText(value) {
  const confidence = Number(value);
  if (!Number.isFinite(confidence)) return '-';
  if (confidence >= 0.85) return '高';
  if (confidence >= 0.65) return '中';
  return '低';
}

export function pageRequest(memberId, currentPage, pageSize) {
  return {
    requestData: { memberId },
    page: { currentPage, pageSize },
  };
}

export function questionnaireValue(profile, key) {
  return profile?.questionnaireList?.find((item) => item.key === key)?.value;
}

export function readableRecordStatus(value) {
  return RECORD_STATUS_LABELS[value] || (value ? '状态已记录' : '-');
}

export function durationText(value) {
  if (value === null || value === undefined) return '-';
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return '-';
  const minutes = Math.round(seconds / 60);
  return minutes < 1 ? '不足 1 分钟' : `${minutes} 分钟`;
}

function timelineContent(record) {
  const content = parseJson(record?.normalizedContent) || {};
  const sourceContent =
    content.sourceContent && typeof content.sourceContent === 'object'
      ? content.sourceContent
      : {};
  return { content, combined: { ...sourceContent, ...content } };
}

function bodyMetricPresentation(record) {
  const { combined } = timelineContent(record);
  const metricCode = String(combined.dimensionsType || 'weight').toLowerCase();
  const definition = BODY_METRIC_PRESENTATION[metricCode] || {
    name: '身体指标',
    unit: '',
  };
  const rawValue =
    metricCode === 'weight' && Number(combined.weight) > 0
      ? combined.weight
      : combined.dimensionsValue;
  return { metricCode, ...definition, value: rawValue };
}

export function timelinePresentation(recordOrEvidenceType) {
  const record =
    recordOrEvidenceType && typeof recordOrEvidenceType === 'object'
      ? recordOrEvidenceType
      : null;
  const evidenceType = record?.evidenceType || recordOrEvidenceType;
  if (evidenceType === 'weight_context_fact' && record) {
    const metric = bodyMetricPresentation(record);
    return {
      title: `${metric.name}打卡`,
      category: '身体记录',
      color: 'cyan',
    };
  }
  return (
    TIMELINE_PRESENTATION[evidenceType] || {
      title: '训练事件',
      category: '训练记录',
      color: 'gray',
    }
  );
}

export function timelineSummary(record, options = {}) {
  const { content } = timelineContent(record);
  const locator = record?.sourceLocator || {};
  switch (record?.evidenceType) {
    case 'course_execution': {
      const title = content.title || `课程 ${locator.roomId || ''}`.trim();
      const duration = durationText(content.classDurationSeconds);
      const hasCompletion =
        content.completeActionPercentage !== null &&
        content.completeActionPercentage !== undefined &&
        content.completeActionPercentage !== '';
      const completion = Number(content.completeActionPercentage);
      return `${title || '训练课程'} · ${duration}${
        options.includeCompletion !== false &&
        hasCompletion &&
        Number.isFinite(completion)
          ? ` · 动作完成 ${completion}%`
          : ''
      }`;
    }
    case 'activity_execution':
      return `${
        content.rawActivityName ||
        ACTIVITY_TYPE_LABELS[content.normalizedActivityType] ||
        '运动'
      } · ${durationText(content.durationSeconds)}${
        content.kcal ? ` · ${content.kcal} 千卡` : ''
      }`;
    case 'member_goal_change':
      return `运动目标由“${textOrDash(
        content.previousGoal,
      )}”调整为“${textOrDash(content.currentGoal)}”`;
    case 'action_motion_observation':
      return `动作 ${locator.actionId || '-'} 的客观评估已经归档`;
    case 'course_motion_observation':
      return `课程 ${locator.roomId || '-'} 的课程评估已经归档`;
    case 'capability_account_snapshot':
      return `${
        DIMENSION_LABELS[locator.dimensionCode] ||
        locator.dimensionCode ||
        '运动能力'
      }数据已更新`;
    case 'weight_context_fact': {
      const metric = bodyMetricPresentation(record);
      const hasValue =
        metric.value !== null &&
        metric.value !== undefined &&
        metric.value !== '' &&
        Number(metric.value) > 0;
      return hasValue
        ? `${metric.name} ${metric.value}${
            metric.unit ? ` ${metric.unit}` : ''
          } 已记录`
        : `${metric.name}已记录`;
    }
    default:
      return `${timelinePresentation(record).title}已记录`;
  }
}

export function timelineDetailRows(record) {
  const { content, combined } = timelineContent(record);
  const isBodyMetric = record?.evidenceType === 'weight_context_fact';
  const bodyMetric = isBodyMetric ? bodyMetricPresentation(record) : null;
  const rows = [];
  Object.entries(TIMELINE_DETAIL_FIELD_LABELS).forEach(([key, label]) => {
    const value = combined[key];
    if (value === null || value === undefined || value === '') return;
    if (typeof value === 'object') return;
    if (
      isBodyMetric &&
      ['weight', 'weightValue'].includes(key) &&
      bodyMetric.metricCode !== 'weight'
    ) {
      return;
    }
    let displayLabel =
      key === 'title' && record?.evidenceType === 'course_execution'
        ? '课程名称'
        : label;
    let displayValue = value;
    if (key.toLowerCase().includes('duration')) {
      displayValue = durationText(value);
    } else if (['eventTime', 'dietTime', 'sleepTime'].includes(key)) {
      displayValue = timeText(value);
    } else if (key === 'dateIndex') {
      const date = new Date(
        /^\d+$/.test(String(value)) ? Number(value) : value,
      );
      displayValue = Number.isNaN(date.getTime())
        ? '-'
        : date.toLocaleDateString();
    } else if (key === 'completeActionPercentage') {
      displayValue = `${value}%`;
    } else if (key === 'kcal') {
      displayValue = `${value} 千卡`;
    } else if (key === 'distanceValue' && content.distanceUnit) {
      displayValue = `${value} ${content.distanceUnit}`;
    } else if (key === 'courseModality') {
      displayValue = COURSE_MODALITY_LABELS[value] || '课程形式已记录';
    } else if (key === 'normalizedActivityType') {
      displayValue = ACTIVITY_TYPE_LABELS[value] || '运动类型已记录';
    } else if (isBodyMetric && key === 'dimensionsType') {
      displayValue = bodyMetric.name;
    } else if (
      isBodyMetric &&
      ['weight', 'weightValue', 'dimensionsValue'].includes(key)
    ) {
      displayLabel = bodyMetric.name;
      displayValue = `${value}${bodyMetric.unit ? ` ${bodyMetric.unit}` : ''}`;
    }
    if (!rows.some((item) => item.label === displayLabel)) {
      rows.push({ label: displayLabel, value: displayValue });
    }
  });
  return rows;
}

export function timelineSourceText(value) {
  const labels = {
    'litta-ptainer': '课程与训练系统',
    'litta-metaspace': '会员运动与打卡',
    'litta-detail': '训练档案系统',
  };
  return labels[value] || '平台训练数据';
}

export function normalizePhone(value) {
  const phone = String(value || '')
    .trim()
    .replace(/\s|-/g, '');
  if (phone.startsWith('+86')) return phone.slice(3);
  if (phone.startsWith('86') && phone.length === 13) return phone.slice(2);
  return phone;
}

export function readWorkspaceQueryState() {
  if (typeof window === 'undefined') return null;
  try {
    return JSON.parse(window.sessionStorage.getItem(WORKSPACE_QUERY_STATE_KEY));
  } catch (error) {
    return null;
  }
}

export function writeWorkspaceQueryState(value) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      WORKSPACE_QUERY_STATE_KEY,
      JSON.stringify(value),
    );
  } catch (error) {
    // 浏览器禁用会话存储时不阻断会员查询主流程。
  }
}

export function metricDifferenceText(series) {
  const points = series?.points || [];
  if (points.length < 2) return '-';
  const difference =
    Number(points[points.length - 1].value) - Number(points[0].value);
  if (!Number.isFinite(difference)) return '-';
  const rounded = Math.round(difference * 100) / 100;
  const value = metricValue(Math.abs(rounded), series.unit, series.metricCode);
  if (rounded > 0) return `+${value}`;
  if (rounded < 0) return `-${value}`;
  return value;
}

export function progressMonthKey(value) {
  const matched = String(value || '').match(/(\d{4})\D?(\d{1,2})/);
  if (!matched) return '';
  return `${matched[1]}-${String(matched[2]).padStart(2, '0')}`;
}

export function sortProgressMonths(records = []) {
  return records
    .slice()
    .sort((left, right) =>
      progressMonthKey(left?.month).localeCompare(
        progressMonthKey(right?.month),
      ),
    );
}

export function trainingRecordDayOfMonth(record) {
  const parseDateMatch = String(record?.parseDate || '').match(
    /^\d{4}[-/]\d{1,2}[-/](\d{1,2})(?:\D|$)/,
  );
  if (parseDateMatch) {
    const day = Number(parseDateMatch[1]);
    if (day >= 1 && day <= 31) return day;
  }

  const dateTextMatch = String(record?.dateText || '').match(
    /(?:^|\D)(\d{1,2})(?:日|$)/,
  );
  if (dateTextMatch) {
    const day = Number(dateTextMatch[1]);
    if (day >= 1 && day <= 31) return day;
  }

  const dayIndex = Number(record?.dayIndex);
  return dayIndex >= 1 && dayIndex <= 31 ? dayIndex : null;
}

export function buildTrainingCalendarMonth(record) {
  const key = progressMonthKey(record?.month);
  if (!key) return null;
  const [year, month] = key.split('-').map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const values = new Map();

  (record.dailySportDataList || []).forEach((item) => {
    const day = trainingRecordDayOfMonth(item);
    if (!day || day > daysInMonth) return;
    const minutes = Math.max(Number(item?.sportMinutes) || 0, 0);
    values.set(day, (values.get(day) || 0) + minutes);
  });

  return {
    key,
    label: `${year}.${String(month).padStart(2, '0')}`,
    cells: [
      ...Array.from({ length: firstWeekday }, (_, index) => ({
        key: `${key}-empty-${index}`,
        empty: true,
      })),
      ...Array.from({ length: daysInMonth }, (_, index) => {
        const day = index + 1;
        return {
          key: `${key}-${day}`,
          day,
          minutes: values.get(day) || 0,
        };
      }),
    ],
  };
}

export function trainingMinuteIntensity(minutes) {
  if (minutes <= 0) return 0;
  if (minutes < 20) return 1;
  if (minutes < 45) return 2;
  if (minutes < 75) return 3;
  return 4;
}

export function monthComparisonText(currentValue, previousValue) {
  const current = Number(currentValue) || 0;
  const previous = Number(previousValue) || 0;
  if (previous <= 0) return '暂无上月对比';
  const percentage = Math.round(((current - previous) / previous) * 100);
  if (percentage > 0) return `较上月 +${percentage}%`;
  if (percentage < 0) return `较上月 ${percentage}%`;
  return '与上月持平';
}
